<template>
  <v-form ref="formRef" v-model="formValid" @submit.prevent>
    <v-card variant="outlined" class="mb-4">
      <v-card-title class="bg-secondary text-white">
        <v-icon class="mr-2">mdi-account-group</v-icon>
        {{ tl('Joint Arrangement Group') }}
      </v-card-title>
      <v-card-text>
        <!-- Warning: not enough JA BOs -->
        <v-alert
          v-if="eligibleBOs.length < 2"
          type="warning"
          variant="tonal"
          class="mb-4"
        >
          <v-icon class="mr-2">mdi-alert</v-icon>
          {{ tl('Group registration requires at least 2 BOs with JointArrangement=True.') }}
          {{ tl('Eligible BOs') }}: {{ eligibleBOs.length }} / {{ tl('Total') }}: {{ registeredBOs.length }}
          <div class="text-caption mt-1">
            {{ tl('Condition: JointArrangement = Yes, Types of BO = CONTROL BY OTHER MEANS') }}
          </div>
        </v-alert>

        <template v-if="eligibleBOs.length >= 2">
          <!-- Add Member Section -->
          <div class="text-subtitle-1 font-weight-bold mb-3">
            <v-icon size="small" class="mr-1">mdi-account-plus</v-icon>
            {{ tl('Add Group Member') }}
          </div>

          <!-- Editing indicator -->
          <v-alert
            v-if="editingMemberIndex !== null"
            type="info"
            variant="tonal"
            density="compact"
            class="mb-3"
          >
            <div class="d-flex align-center">
              <v-icon size="18" class="mr-2">mdi-pencil</v-icon>
              <span>{{ tl('Editing') }} <strong>{{ members[editingMemberIndex]?.name }}</strong> - {{ tl('Voting Rights (%)') }}</span>
              <v-spacer />
              <v-btn size="x-small" variant="text" @click="cancelEditMember">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </div>
          </v-alert>

          <v-row>
            <!-- BO Name Select (hidden when editing) -->
            <v-col v-if="editingMemberIndex === null" cols="12">
              <v-select
                v-model="selectedBO"
                :items="availableBOs"
                item-title="displayName"
                item-value="identificationNumber"
                :label="tl('Name of Beneficial Owner')"
                :disabled="props.readOnly"
                return-object
                clearable
                variant="outlined"
                density="compact"
              >
                <template #item="{ props: itemProps, item }">
                  <v-list-item v-bind="itemProps">
                    <template #subtitle>
                      {{ item.raw.identificationType }}: {{ item.raw.identificationNumber }}
                    </template>
                  </v-list-item>
                </template>
              </v-select>
            </v-col>

            <!-- Types of BO (readonly) -->
            <v-col cols="6">
              <v-text-field
                model-value="CONTROL BY OTHER MEANS"
                :label="tl('Types of BO')"
                readonly
                variant="outlined"
                density="compact"
                bg-color="grey-lighten-4"
              />
            </v-col>

            <!-- Criteria E (readonly checkbox) -->
            <v-col cols="6">
              <v-checkbox
                :model-value="true"
                :label="tl('Criteria E')"
                readonly
                density="compact"
                hide-details
                class="mt-1"
              >
                <template #label>
                  <span class="text-caption">
                    {{ tl('Criteria E - Controls majority voting rights under agreement') }}
                  </span>
                </template>
              </v-checkbox>
            </v-col>

            <!-- Voting Rights -->
            <v-col cols="6">
              <v-text-field
                v-model.number="votingRights"
                :label="tl('Voting Rights (%)')"
                type="number"
                min="0"
                max="100"
                step="0.0001"
                :disabled="props.readOnly || (editingMemberIndex === null && !selectedBO)"
                :rules="votingRightsRules"
                variant="outlined"
                density="compact"
                :hint="`0.0001 ~ 100.0000 (${tl('remaining')}: ${remainingVotingRights.toFixed(4)}%)`"
                persistent-hint
              />
            </v-col>

            <!-- Add / Save Button -->
            <v-col cols="6" class="d-flex align-center gap-2">
              <v-btn
                v-if="editingMemberIndex !== null"
                color="warning"
                variant="flat"
                :disabled="props.readOnly || votingRights === null"
                @click="addMember"
              >
                <v-icon start>mdi-check</v-icon>
                {{ tl('Save Changes') }}
              </v-btn>
              <v-btn
                v-if="editingMemberIndex !== null"
                variant="outlined"
                color="secondary"
                @click="cancelEditMember"
              >
                {{ tl('Cancel') }}
              </v-btn>
              <v-btn
                v-if="editingMemberIndex === null"
                color="success"
                variant="flat"
                :disabled="props.readOnly || !selectedBO || votingRights === null"
                @click="addMember"
              >
                <v-icon start>mdi-plus</v-icon>
                {{ tl('Add to Group') }}
              </v-btn>
            </v-col>
          </v-row>

          <v-divider class="my-4" />

          <!-- Group Name -->
          <div class="text-subtitle-1 font-weight-bold mb-3">
            <v-icon size="small" class="mr-1">mdi-account-group</v-icon>
            {{ tl('Group Information') }}
          </div>

          <v-row>
            <v-col cols="12" md="8">
              <v-text-field
                v-model="groupName"
                :label="tl('Group Name') + ' *'"
                :rules="groupNameRules"
                :disabled="props.readOnly"
                variant="outlined"
                density="compact"
                maxlength="200"
                required
                @input="groupName = groupName.toUpperCase()"
              />
            </v-col>
          </v-row>

          <v-divider class="my-4" />

          <!-- Members Table -->
          <div class="text-subtitle-1 font-weight-bold mb-3">
            <v-icon size="small" class="mr-1">mdi-table</v-icon>
            {{ tl('Joint Arrangement Members') }} ({{ members.length }})
          </div>

          <v-table v-if="members.length > 0" density="compact">
            <thead>
              <tr>
                <th>{{ tl('Name') }}</th>
                <th>{{ tl('Identification No.') }}</th>
                <th>{{ tl('Voting Rights (%)') }}</th>
                <th v-if="!props.readOnly" width="80">{{ tl('Action') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(member, idx) in members"
                :key="idx"
                :class="{ 'bg-warning-lighten-5': editingMemberIndex === idx }"
              >
                <td>{{ member.name }}</td>
                <td>{{ member.idNo }}</td>
                <td>{{ member.votingRights.toFixed(4) }}%</td>
                <td v-if="!props.readOnly">
                  <v-btn
                    icon="mdi-pencil"
                    size="x-small"
                    color="primary"
                    variant="text"
                    @click="editMember(idx)"
                  />
                  <v-btn
                    icon="mdi-delete"
                    size="x-small"
                    color="error"
                    variant="text"
                    @click="removeMember(idx)"
                  />
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td colspan="2" class="text-right font-weight-bold">{{ tl('Total Voting Rights') }}:</td>
                <td :class="{ 'text-error': totalVotingRights > 100 }">
                  {{ totalVotingRights.toFixed(4) }}%
                </td>
                <td v-if="!props.readOnly" />
              </tr>
            </tfoot>
          </v-table>

          <v-alert
            v-else
            type="info"
            variant="tonal"
            density="compact"
          >
            {{ tl('No members added yet. Select a BO above and click "Add to Group".') }}
          </v-alert>

          <!-- Validation: min 2 members -->
          <v-alert
            v-if="members.length > 0 && members.length < 2"
            type="warning"
            variant="tonal"
            density="compact"
            class="mt-3"
          >
            {{ tl('At least 2 members are required.') }} ({{ tl('Current') }}: {{ members.length }})
          </v-alert>

          <!-- Validation: total shares must be > 50% -->
          <v-alert
            v-if="members.length >= 2 && totalVotingRights <= 50"
            type="warning"
            variant="tonal"
            density="compact"
            class="mt-3"
          >
            {{ tl('Group Shares must be more than 50%.') }} ({{ tl('Current') }}: {{ totalVotingRights.toFixed(4) }}%)
          </v-alert>

          <!-- Validation: total shares > 100% -->
          <v-alert
            v-if="totalVotingRights > 100"
            type="error"
            variant="tonal"
            density="compact"
            class="mt-3"
          >
            {{ tl('Total Voting Rights cannot exceed 100%.') }} ({{ tl('Current') }}: {{ totalVotingRights.toFixed(4) }}%)
          </v-alert>
        </template>
      </v-card-text>
    </v-card>
  </v-form>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';

// Translation
const languageStore = useLanguageStore();
const { createSyncT } = useTranslation();
const t = createSyncT(languageStore.currentLanguage, { silent: true });
const tl = (text: string) => {
  if (!text) return '';
  const translated = t(`startTask.${text}`);
  return (translated && translated !== `startTask.${text}`) ? translated : text;
};

interface RegisteredBO {
  personName: string;
  identificationNumber: string;
  identificationType: string;
  typesOfBO: string;
  boType: string;
  jointArrangement?: boolean;
}

interface SelectableBO extends RegisteredBO {
  displayName: string;
}

interface GroupMember {
  name: string;
  idNo: string;
  idType: string;
  votingRights: number;
  boValue: string; // SSM4U dropdown value (e.g., "2034171,EBOS_TRANS_DATA")
}

interface Props {
  registeredBOs: RegisteredBO[];
  readOnly?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  registeredBOs: () => [],
  readOnly: false,
});

const emit = defineEmits<{
  (e: 'update:formData', data: any): void;
  (e: 'update:valid', valid: boolean): void;
}>();

// Form state
const formRef = ref<any>(null);
const formValid = ref(false);

// Add member state
const selectedBO = ref<SelectableBO | null>(null);
const votingRights = ref<number | null>(0);
const editingMemberIndex = ref<number | null>(null);

// Group state
const groupName = ref('');
const members = ref<GroupMember[]>([]);

// Eligible BOs: JointArrangement=True AND TypesOfBO=CONTROL BY OTHER MEANS
const eligibleBOs = computed(() =>
  props.registeredBOs.filter(bo => {
    const isJA = bo.jointArrangement === true;
    const isControl = (bo.typesOfBO || '').toUpperCase().includes('CONTROL')
      || bo.typesOfBO === 'C';
    return isJA && isControl;
  }),
);

// Available BOs: eligible minus already added
const availableBOs = computed<SelectableBO[]>(() => {
  const addedIds = new Set(members.value.map(m => m.idNo));
  return eligibleBOs.value
    .filter(bo => !addedIds.has(bo.identificationNumber))
    .map(bo => ({
      ...bo,
      displayName: `${bo.personName} (${bo.identificationNumber})`,
    }));
});

// Total voting rights
const totalVotingRights = computed(() =>
  members.value.reduce((sum, m) => sum + m.votingRights, 0),
);

// Validation rules
// Remaining available voting rights (exclude editing member's current rights)
const remainingVotingRights = computed(() => {
  let total = totalVotingRights.value;
  if (editingMemberIndex.value !== null) {
    total -= members.value[editingMemberIndex.value]?.votingRights || 0;
  }
  return Math.max(0, 100 - total);
});

const votingRightsRules = [
  (v: any) => v !== null && v !== '' || tl('Voting Rights is required'),
  (v: any) => Number(v) > 0 || tl('Voting Rights must be greater than 0'),
  (v: any) => Number(v) <= 100 || tl('Voting Rights cannot exceed 100%'),
  (v: any) => Number(v) <= remainingVotingRights.value || `${tl('Remaining available')}: ${remainingVotingRights.value.toFixed(4)}% (${tl('total cannot exceed 100%')})`,
];

const groupNameRules = [
  (v: string) => !!v || tl('Group Name is required'),
  (v: string) => (v && v.length >= 2) || tl('Must be at least 2 characters'),
];

// Add or update member in group
const addMember = () => {
  const vr = Number(votingRights.value);
  if (vr <= 0 || vr > 100) return;

  if (editingMemberIndex.value !== null) {
    // Update existing member's voting rights
    const idx = editingMemberIndex.value;
    const currentVr = members.value[idx].votingRights;
    const otherTotal = totalVotingRights.value - currentVr;
    if (otherTotal + vr > 100) return;

    members.value[idx] = { ...members.value[idx], votingRights: vr };
    editingMemberIndex.value = null;
    selectedBO.value = null;
    votingRights.value = 0;
    return;
  }

  if (!selectedBO.value) return;
  if (totalVotingRights.value + vr > 100) return;

  members.value.push({
    name: selectedBO.value.personName,
    idNo: selectedBO.value.identificationNumber,
    idType: selectedBO.value.identificationType,
    votingRights: vr,
    boValue: selectedBO.value.identificationNumber,
  });

  // Reset for next member
  selectedBO.value = null;
  votingRights.value = 0;
};

// Edit member: load voting rights into input
const editMember = (idx: number) => {
  editingMemberIndex.value = idx;
  votingRights.value = members.value[idx].votingRights;
  selectedBO.value = null; // BO select hidden during edit
};

// Cancel editing member
const cancelEditMember = () => {
  editingMemberIndex.value = null;
  selectedBO.value = null;
  votingRights.value = 0;
};

// Remove member
const removeMember = (idx: number) => {
  if (editingMemberIndex.value === idx) {
    cancelEditMember();
  } else if (editingMemberIndex.value !== null && editingMemberIndex.value > idx) {
    editingMemberIndex.value--;
  }
  members.value.splice(idx, 1);
};

// Watch for changes → emit
watch([members, groupName], () => {
  const data = getFormData();
  emit('update:formData', data);
  emit('update:valid', validateSync());
}, { deep: true });

// Sync validation (no async)
const validateSync = (): boolean => {
  if (members.value.length < 2) return false;
  if (!groupName.value || groupName.value.trim().length < 2) return false;
  if (members.value.some(m => m.votingRights <= 0 || m.votingRights > 100)) return false;
  if (totalVotingRights.value <= 50) return false;
  if (totalVotingRights.value > 100) return false;
  return true;
};

// Public API
const validate = async (): Promise<boolean> => {
  return validateSync();
};

const getValidationErrors = (): string[] => {
  const errors: string[] = [];
  if (members.value.length < 2) {
    errors.push(tl('At least 2 members are required for group arrangement'));
  }
  if (!groupName.value || groupName.value.trim().length < 2) {
    errors.push(tl('Group Name is required'));
  }
  for (const m of members.value) {
    if (m.votingRights <= 0) {
      errors.push(`${m.name}: ${tl('Voting Rights must be greater than 0')}`);
    }
    if (m.votingRights > 100) {
      errors.push(`${m.name}: ${tl('Voting Rights cannot exceed 100%')}`);
    }
  }
  if (totalVotingRights.value <= 50) {
    errors.push(`${tl('Group Shares must be more than 50%.')} (${tl('Current')}: ${totalVotingRights.value.toFixed(4)}%)`);
  }
  if (totalVotingRights.value > 100) {
    errors.push(`${tl('Total Voting Rights cannot exceed 100%.')} (${tl('Current')}: ${totalVotingRights.value.toFixed(4)}%)`);
  }
  return errors;
};

const getFormData = () => ({
  groupName: groupName.value,
  members: members.value.map(m => ({
    name: m.name,
    idNo: m.idNo,
    idType: m.idType,
    votingRights: m.votingRights,
  })),
  totalVotingRights: totalVotingRights.value,
  selectedBOIds: members.value.map(m => m.idNo),
});

const resetForm = () => {
  members.value = [];
  groupName.value = '';
  selectedBO.value = null;
  votingRights.value = 0;
  editingMemberIndex.value = null;
};

const loadSavedData = (savedData: any) => {
  if (savedData?.groupName) {
    groupName.value = savedData.groupName;
  }
  if (savedData?.members) {
    members.value = savedData.members;
  }
};

defineExpose({
  validate,
  getValidationErrors,
  getFormData,
  resetForm,
  loadSavedData,
});
</script>
